import { tradePriceToDollars } from '@/utils/market-outcomes';

/** 订单簿每侧档位数（请求 level / UI 截取对齐） */
export const ORDER_BOOK_DEPTH_LEVEL = 100;

/** 点差展示兜底（美分）；无买卖一时 spread 用此格式化 fallback */
export const ORDER_BOOK_SPREAD_CENTS = 1;

export const EMPTY_DEPTH_BOOK = Object.freeze({
  asks: [],
  bids: [],
  spreadLabel: '—',
  lastPriceLabel: '—',
  isEmpty: true,
});

function normalizeDepthPriceCents(raw) {
  const dollars = tradePriceToDollars(raw);
  if (!Number.isFinite(dollars) || dollars <= 0) return null;
  return Math.round(dollars * 1000) / 10;
}

function formatDepthPriceCents(cents) {
  if (cents == null || !Number.isFinite(cents)) return '—';
  const scaled = Math.round(cents * 10) / 10;
  return `${scaled.toFixed(1)}¢`;
}

function applyDepthBars(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const maxQty = list.reduce((max, row) => {
    const qty = Number(row?.quantity);
    return Number.isFinite(qty) && qty > max ? qty : max;
  }, 0);

  return list.map((row) => {
    const qty = Number(row?.quantity);
    const depthBarPercent =
      maxQty > 0 && Number.isFinite(qty) && qty > 0 ? Math.round((qty / maxQty) * 100) : 0;
    return { ...row, depthBarPercent };
  });
}

function mapDepthSideRows(list, level = ORDER_BOOK_DEPTH_LEVEL, side = 'bid') {
  const rows = (Array.isArray(list) ? list : [])
    .filter((row) => row && row.price != null)
    .map((row) => {
      const priceCents = normalizeDepthPriceCents(row.price);
      const qty = Number(row.quantity);
      const total = priceCents != null && Number.isFinite(qty) ? (priceCents / 100) * qty : null;
      return {
        priceLabel: formatDepthPriceCents(priceCents),
        priceCents,
        quantity: Number.isFinite(qty) ? qty : null,
        quantityLabel: Number.isFinite(qty)
          ? qty.toLocaleString('en-US', { maximumFractionDigits: 2 })
          : '—',
        totalLabel: total != null ? `$${total.toFixed(2)}` : '—',
        depthBarPercent: 0,
      };
    })
    .filter((row) => row.priceCents != null && Number.isFinite(row.quantity) && row.quantity > 0);

  const sorted =
    side === 'ask'
      ? rows.sort((a, b) => (a.priceCents ?? 0) - (b.priceCents ?? 0))
      : rows.sort((a, b) => (b.priceCents ?? 0) - (a.priceCents ?? 0));

  return applyDepthBars(sorted.slice(0, level));
}

/** 将 depth 接口响应映射为 Polymarket 风格订单簿数据 */
export function mapDepthBook(payload, level = ORDER_BOOK_DEPTH_LEVEL) {
  const asks = mapDepthSideRows(payload?.asks, level, 'ask');
  const bids = mapDepthSideRows(payload?.bids, level, 'bid');

  if (!asks.length && !bids.length) {
    return { ...EMPTY_DEPTH_BOOK };
  }

  const askPrices = asks.map((row) => row.priceCents).filter((n) => n != null);
  const bidPrices = bids.map((row) => row.priceCents).filter((n) => n != null);
  const bestAsk = askPrices.length ? Math.min(...askPrices) : null;
  const bestBid = bidPrices.length ? Math.max(...bidPrices) : null;

  let spreadLabel = '—';
  if (bestAsk != null && bestBid != null) {
    spreadLabel = formatDepthPriceCents(Math.max(0, bestAsk - bestBid));
  }

  const lastPriceLabel =
    bestBid != null
      ? formatDepthPriceCents(bestBid)
      : bestAsk != null
      ? formatDepthPriceCents(bestAsk)
      : '—';

  const lastTradeRaw = payload?.lastTradePrice ?? payload?.last_trade_price;
  const lastTradeCents = normalizeDepthPriceCents(lastTradeRaw);

  return {
    asks: [...asks].sort((a, b) => (b.priceCents ?? 0) - (a.priceCents ?? 0)),
    bids: [...bids].sort((a, b) => (b.priceCents ?? 0) - (a.priceCents ?? 0)),
    spreadLabel,
    lastPriceLabel,
    lastTradeCents,
    isEmpty: false,
  };
}

function sideRowsEqual(a, b) {
  if (a === b) return true;
  if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
  // Structural only: price + qty. Ignore depthBarPercent / labels so HTTP
  // reconcile and WS full snapshots that differ only in cosmetics do not
  // replace the book (scroll re-anchor flash).
  return a.every((row, i) => {
    const other = b[i];
    const pa = Number(row?.priceCents);
    const pb = Number(other?.priceCents);
    const qa = Number(row?.quantity);
    const qb = Number(other?.quantity);
    if (!Number.isFinite(pa) || !Number.isFinite(pb) || pa !== pb) return false;
    if (!Number.isFinite(qa) || !Number.isFinite(qb)) return false;
    return Math.abs(qa - qb) < 1e-9;
  });
}

/** 订单簿结构（价+量）未变化时不替换 ref，避免对账/WS 闪烁 */
export function depthBooksEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.isEmpty === b.isEmpty && sideRowsEqual(a.asks, b.asks) && sideRowsEqual(a.bids, b.bids);
}

export function assignDepthBook(targetRef, nextBook) {
  const next = nextBook ?? { ...EMPTY_DEPTH_BOOK };
  if (depthBooksEqual(targetRef.value, next)) return;
  targetRef.value = next;
}

/** @deprecated 旧表格行格式，保留兼容 */
export function mapDepthRows(payload, level = ORDER_BOOK_DEPTH_LEVEL) {
  const book = mapDepthBook(payload, level);
  const list = book.asks.length ? book.asks : book.bids;
  return list.map((row) => ({
    price: row.priceLabel,
    vol: row.quantityLabel,
  }));
}
